import { ColumnDef } from "@tanstack/react-table";
import { GenericError } from "@via/components/error.tsx";
import { SkeletonLoader } from "@via/components/loading/loading.tsx";
import Table from "@via/components/table/table.tsx";
import { type ReportTableState } from "@via/pages/projects/runs/reports/hooks/use-report-table.ts";
import { DownloadableReport } from "@via/pages/projects/runs/reports/report-types/downloadable-report.tsx";
import {
  TABULAR_PREVIEW_BYTES,
  TABULAR_PREVIEW_ROWS,
} from "@via/pages/projects/runs/reports/utils/constants.ts";
import {
  cleanTabularDataBeforeRender,
  describeFullTableLoad,
  customSortingFn,
  dropTrailingPartialLine,
  findType,
  getColumnsTabularData,
  getReportUrl,
  processPercentData,
} from "@via/pages/projects/runs/reports/utils/helpers.ts";
import { useFetchRunReportTabularPreview } from "@via/pages/projects/runs/runs.api.tsx";
import { Tooltip } from "react-tooltip";
import { RunReportResource } from "common";
import { JSX, useEffect, useRef, useState } from "react";

/**
 * Shared shape-builder for a single table column, used by both the preview
 * path (a Record-keyed sample row) and the worker-streamed path (a
 * column-ordered array sample row), so the two can't drift out of sync.
 */
const buildColumnDef = (field: string, sampleValue: unknown) => {
  const type = findType((sampleValue ?? "") as string);
  return {
    size: 150,
    minSize: 100,
    accessorKey: field,
    header: field,
    cell: (info: any) => info.getValue(),
    type,
    sortingFn: customSortingFn(type),
  };
};

/** Tracks incremental progress for the worker-streamed (non-preview) path. */
interface FullTableCursor {
  url: string;
  convertedCount: number;
  columnsBuilt: boolean;
  /** Converted rows not yet handed to the table (see `shouldPublishRows`). */
  pending: Record<string, unknown>[];
  /** How many rows the table currently holds. */
  publishedCount: number;
}

/**
 * Whether the converted-but-unpublished rows should be handed to the table
 * now. Every new `data` array makes TanStack Table rebuild its core, filtered,
 * sorted and paginated row models over ALL rows, so publishing every 2000 row
 * batch of a 520,000 row file rebuilt the models 260 times (about 68 million
 * row builds, most of the full-load time). Publishing only once the pending
 * rows at least double what the table holds keeps the total rebuild work
 * linear in the row count: about ten rebuilds for that file. The first batch
 * still shows at once, and the "Loading N rows" counter tracks every batch.
 */
export const shouldPublishRows = (
  pendingCount: number,
  publishedCount: number,
  isComplete: boolean
): boolean =>
  pendingCount > 0 &&
  (isComplete || publishedCount === 0 || pendingCount >= publishedCount);

const reportViewKey = (url: string, previewOnly: boolean): string =>
  `${previewOnly ? "preview" : "full"}:${url}`;

/** The `data`/`columns`/`previewUnavailable` state as it currently sits in
 * `useState`, plus a tag recording which `${mode}:${url}` identity it was
 * last confirmed to have been computed FOR. */
export interface RawTabularReportState {
  data: Record<string, unknown>[];
  columns: ColumnDef<any>[];
  previewUnavailable: boolean;
  settledKey: string;
}

export interface VisibleTabularReportState {
  data: Record<string, unknown>[];
  columns: ColumnDef<any>[];
  previewUnavailable: boolean;
  /** False whenever `raw` is still (or might still be) the PREVIOUS report's
   * settled state. Every render path — skeleton, error, the table itself,
   * the download-fallback — must treat this exactly like "still loading",
   * never like "empty result", so a stale value can never be mistaken for
   * the new report's actual (possibly genuinely empty) data. */
  isSettledForCurrentReport: boolean;
}

/**
 * Pure gate `TabularReport` reads through on every render before showing
 * ANYTHING derived from `data`/`columns`/`previewUnavailable`.
 *
 * WHY `data`/`columns`/`previewUnavailable` are still written by effects,
 * not derived inline with `useMemo`: building them involves real,
 * non-trivial work — parsing a preview prefix, or incrementally converting
 * only the worker rows that arrived since the last batch (see the cursor
 * effect below) — that has to run somewhere a Web Worker's `onmessage` or a
 * per-parse cursor can live, which an effect (or the `useReportTable` hook
 * itself) is. A `useMemo` gains nothing here: it would still need the exact
 * same "was `worker` already being read for a DIFFERENT url the render
 * before this one" bookkeeping the cursor effect already does (see the
 * FRAGILE ORDERING comment below) to avoid re-latching stale data, and
 * hand-rolling that as a ref mutated directly during render turns out NOT to
 * be reliably idempotent under React 18 StrictMode's double-invoked render
 * bodies (verified empirically) the way a `useEffect` mutation is (its
 * "already-processed" flags make a second, StrictMode-doubled invocation of
 * the SAME commit a no-op) — so the incremental bookkeeping stays in
 * effects, unchanged in shape from before this fix.
 *
 * What effects CANNOT guarantee is landing their write in the SAME commit as
 * the `url`/`previewOnly` change that makes it relevant — an effect always
 * runs strictly AFTER the render/commit that changed its dependencies. So
 * for exactly one commit, `data`/`columns`/`previewUnavailable` can still
 * hold the PREVIOUS report's fully-settled values while `url`/`previewOnly`
 * already point at a new one.
 *
 * `settledKeyRef` (see below) fixes this not by making the effects run any
 * faster, but by making that staleness impossible to RENDER: it's a ref,
 * advanced by those same effects at the exact point they've confirmed their
 * write is safe for the CURRENT `url`/`previewOnly` (mirroring the
 * `wasActivelyTrackingADifferentUrl` guard that already protects the
 * cursor's own bookkeeping from corruption), and `deriveVisibleReportState`
 * is the single gate every render path reads through: whenever the identity
 * the ref was last advanced for doesn't match THIS render's `url`/
 * `previewOnly`, it hands back an empty, not-yet-settled view instead of the
 * raw state — so a stale value can sit in `useState` for a whole extra
 * commit without ever reaching the DOM. Kept as a small, dependency-free
 * pure function (rather than inlined) specifically so this invariant —
 * "a mismatched identity's data is NEVER handed back" — is directly
 * unit-testable by calling it with mismatched inputs, without needing to
 * fight React's effect-flushing to observe an intermediate commit (see the
 * "render-safety" tests in tabular-report.test.tsx, including one that
 * proves the invariant by breaking it).
 */
export function deriveVisibleReportState(
  raw: RawTabularReportState,
  target: { url: string; previewOnly: boolean }
): VisibleTabularReportState {
  if (raw.settledKey !== reportViewKey(target.url, target.previewOnly)) {
    return {
      data: [],
      columns: [],
      previewUnavailable: false,
      isSettledForCurrentReport: false,
    };
  }
  return {
    data: raw.data,
    columns: raw.columns,
    previewUnavailable: raw.previewUnavailable,
    isSettledForCurrentReport: true,
  };
}

export function TabularReport({
  report,
  previewOnly = false,
  worker,
  onLoadFullTable,
}: {
  report: RunReportResource;
  previewOnly?: boolean;
  /** Present for an oversized table: switches from the preview to a full load. */
  onLoadFullTable?: () => void;
  /**
   * Owned by the PARENT (`ReportViewerContent` in report-viewer.tsx), which
   * calls `useReportTable` exactly once per file and shares the result with
   * both this table AND the "Download with Percentages" export — a single
   * worker per file rather than one per consumer. The parent's `enabled`
   * flag already folds in `!previewOnly`, so whenever `previewOnly` is true
   * this arrives as the hook's `EMPTY`/never-loading snapshot; the effect
   * below is written to tolerate that (it simply never fires its
   * `!previewOnly` branch).
   */
  worker: ReportTableState;
}): JSX.Element {
  const [data, setData] = useState<Record<string, unknown>[]>([]);
  const [columns, setColumns] = useState<ColumnDef<any>[]>([]);
  const [previewUnavailable, setPreviewUnavailable] = useState(false);
  const url = getReportUrl(report.routePath);

  // The `${mode}:${url}` identity `data`/`columns`/`previewUnavailable`
  // currently correspond to, as of their last write. Advanced by the two
  // effects below (never by render); read synchronously at render time via
  // `deriveVisibleReportState`. See that function's doc comment for the full
  // rationale.
  const settledKeyRef = useRef<string>("");

  // Preview path (Task 5) — unchanged. A single already-truncated 2MB
  // prefix is fetched and parsed synchronously on the main thread; it stays
  // this way deliberately, since previewOnly only ever applies to files
  // large enough that even the truncated prefix is a small, bounded parse.
  const previewQuery = useFetchRunReportTabularPreview(
    url,
    TABULAR_PREVIEW_BYTES,
    { enabled: previewOnly }
  );
  const {
    data: runReportTabularData,
    isLoading: isPreviewLoading,
    isError: isPreviewError,
  } = previewQuery;

  const previewCursorRef = useRef<string>("");

  useEffect(() => {
    if (!previewOnly) return;

    // Mark this identity settled up front — unlike the full-table `worker`
    // snapshot below, `runReportTabularData` is NEVER a stale snapshot of a
    // PREVIOUS report the way a hand-rolled useState+useEffect hook's return
    // value can be (react-query computes it synchronously from the CURRENT
    // queryKey, which includes `url`, during render — see the longer note a
    // few lines down). So whatever this effect computes below for `url`
    // (even "still nothing, still loading") is already correct FOR `url`;
    // there's nothing to defer the way the full-table effect has to.
    settledKeyRef.current = reportViewKey(url, previewOnly);

    // A new preview target — a different report, or switching into preview
    // mode from the full-table path — starts from a clean slate, so a
    // stale `data`/`columns`/`previewUnavailable` from a PREVIOUS
    // report/mode never leaks into the new one. This also covers entering
    // preview mode from the full-table path: the full-table cursor effect
    // below early-returns entirely when `previewOnly` is true, so this is
    // the only place that clears a stale full-table `data`/`columns` left
    // over from before the switch.
    //
    // Unlike the full-table `worker` snapshot below, it's safe to reset AND
    // immediately re-check `runReportTabularData` in the SAME pass, without
    // deferring to a later effect run: react-query computes its returned
    // value synchronously from the CURRENT queryKey (which includes `url`)
    // during render, so `runReportTabularData` here is never a stale
    // snapshot of the OLD report the way a hand-rolled useState+useEffect
    // hook's return value can be — it's already either `undefined` (still
    // loading, in which case the `if (!runReportTabularData) return` below
    // naturally waits for the next pass) or the NEW url's real data.
    if (previewCursorRef.current !== url) {
      previewCursorRef.current = url;
      setData([]);
      setColumns([]);
      setPreviewUnavailable(false);
    }

    if (!runReportTabularData) return;

    const sourceText = dropTrailingPartialLine(runReportTabularData);

    // A very wide file whose header alone exceeds the preview byte budget
    // yields no complete data row (or not even a complete header). Fall
    // back to a download prompt rather than rendering a broken table.
    if (sourceText.split("\n").length < 2) {
      setPreviewUnavailable(true);
      setColumns([]);
      setData([]);
      return;
    }
    setPreviewUnavailable(false);

    const previewColumns = getColumnsTabularData(sourceText);
    let rows = cleanTabularDataBeforeRender(
      sourceText,
      previewColumns
    ) as Record<string, unknown>[];
    rows = rows.slice(0, TABULAR_PREVIEW_ROWS);

    setColumns(
      previewColumns.map(field => buildColumnDef(field, rows?.[0]?.[field]))
    );
    setData(
      report.reportTypes.includes("table-percent")
        ? processPercentData(rows)
        : rows
    );
  }, [runReportTabularData, previewOnly, url]);

  const fullTableCursorRef = useRef<FullTableCursor>({
    url: "",
    convertedCount: 0,
    columnsBuilt: false,
    pending: [],
    publishedCount: 0,
  });

  useEffect(() => {
    if (previewOnly) {
      // While in preview mode, the PARENT (report-viewer.tsx) calls
      // `useReportTable` with `enabled=false`, and its OWN effect resets its
      // state to EMPTY as soon as it's disabled (see use-report-table.ts).
      // So by the time we come BACK to full-table mode, the `worker` prop is
      // guaranteed to already be a fresh EMPTY snapshot, not a stale
      // leftover from whatever url was active before entering preview mode.
      // Forget the url we were tracking so that re-entering full-table mode
      // below is treated as a fresh start (processed immediately, see
      // `wasActivelyTracking...`) rather than deferred as if it were a
      // same-mode url change.
      fullTableCursorRef.current.url = "";
      return;
    }

    // A new parse target — a different report, or switching into the
    // full-table path — starts from a clean slate. `worker.rows`/`columns`
    // already reset to empty inside useReportTable whenever `url` changes;
    // this mirrors that here so a stale converted-row count, ColumnDef set,
    // or `previewUnavailable` flag from a PREVIOUS file/mode never leaks
    // into the new one.
    //
    // FRAGILE ORDERING (do not remove the `if (...) return` below):
    // `worker` is now a PROP, owned by the parent's `useReportTable` call
    // (report-viewer.tsx), not a hook called directly in this component —
    // but the hazard this guards against is unchanged, because it was never
    // actually about which fiber owns the hook. `useReportTable`'s internal
    // effect resets ITS OWN state via `setState`, and a `setState` call
    // inside an effect only *schedules* a future render; it can never
    // retroactively change the `worker` PROP value THIS component's render
    // (and this effect's closure over it) already received for the CURRENT
    // commit. So on the very pass where `url` changes WHILE the parent's
    // `useReportTable` was ALREADY actively enabled for a different url,
    // both the parent's render AND this effect still see `worker.columns`/
    // `worker.rows` as a snapshot of the OLD report's data — the reset is
    // only visible starting the NEXT commit, once the parent's effect has
    // actually run and its `setState` has flowed back down as a new prop.
    // Reading the stale snapshot here (instead of bailing out) would latch
    // the old report's columns as `columnsBuilt` and seed `convertedCount`
    // from the old report's row count — since `columnsBuilt` is never
    // rebuilt until the NEXT url change, the new report's real columns
    // would then never be applied (stale headers shown forever), and rows
    // can be silently dropped whenever the new file has fewer rows than the
    // old one. Deferring the build to the NEXT effect run (once the parent's
    // `useReportTable` reset has actually committed and flowed down as a
    // fresh `worker` prop reflecting the new, empty-then-progressively-
    // filling parse) avoids that — no rows are lost, since `convertedCount`
    // stays 0 until that pass and `worker.rows` at that point only ever
    // contains the NEW parse's rows.
    //
    // This hazard only exists when `worker` was ALREADY being read this
    // same way (enabled, for a DIFFERENT url) in the immediately preceding
    // render — i.e. `fullTableCursorRef.current.url` is a real, non-""
    // url. On a true first mount, or when arriving fresh from preview mode
    // (both leave `.url` as "" above), `worker` is already safe to read on
    // this very pass, so there is nothing to defer.
    const wasActivelyTrackingADifferentUrl =
      fullTableCursorRef.current.url !== "" &&
      fullTableCursorRef.current.url !== url;

    if (fullTableCursorRef.current.url !== url) {
      fullTableCursorRef.current = {
        url,
        convertedCount: 0,
        columnsBuilt: false,
        pending: [],
        publishedCount: 0,
      };
      setData([]);
      setColumns([]);
      setPreviewUnavailable(false);
      if (wasActivelyTrackingADifferentUrl) return;
    }

    // Only mark the identity settled once we're actually about to read
    // `worker` for real (i.e. we did NOT defer above). This is what makes
    // `deriveVisibleReportState` correctly treat the deferred commit — and
    // every commit before this effect has run at all — as "not yet settled
    // for `url`", so the render can never show `data`/`columns` that are
    // either mid-reset or still keyed to the OLD url, regardless of what
    // `worker.isLoading` happens to read on that commit.
    settledKeyRef.current = reportViewKey(url, previewOnly);

    const cursor = fullTableCursorRef.current;

    // Build the ColumnDef array exactly once per parse: as soon as there is
    // a sample row to infer each column's type from, or once parsing has
    // finished with no rows at all (an empty/header-only file). Rebuilding
    // this on every batch would be wasted work — the column set and
    // inferred types never change after the first row.
    if (
      !cursor.columnsBuilt &&
      worker.columns.length > 0 &&
      (worker.rows.length > 0 || worker.isComplete)
    ) {
      const firstRow = worker.rows[0];
      setColumns(
        worker.columns.map((field, index) =>
          buildColumnDef(field, firstRow?.[index])
        )
      );
      cursor.columnsBuilt = true;
    }

    // Convert only the rows that arrived since the last run, not the whole
    // accumulated `worker.rows` array. `worker.rows` only ever grows within
    // one parse, so re-deriving Record objects from the full array on every
    // batch would redo all previously-converted rows every time: for a
    // 15k-row file split into ~8 batches that is ~4.8x the necessary work
    // (2000 + 4000 + ... + 16000 conversions instead of 15000), and it would
    // run synchronously on the main thread on every single batch — the
    // exact class of work this feature moved off the main thread in the
    // first place. Slicing from the cursor keeps this O(total rows) overall.
    if (worker.rows.length > cursor.convertedCount) {
      const newRows = worker.rows.slice(cursor.convertedCount);
      cursor.convertedCount = worker.rows.length;

      const newRecords = newRows.map(row => {
        const record: Record<string, unknown> = {};
        for (let j = 0; j < worker.columns.length; j++) {
          record[worker.columns[j]] = row[j];
        }
        return record;
      });

      const processedRecords = report.reportTypes.includes("table-percent")
        ? // Safe to apply per-batch: processPercentData only ever reads a
          // row's OWN keys/values (its divider column), never other rows',
          // so processing new rows in slices is equivalent to processing
          // the whole array at once.
          processPercentData(newRecords)
        : newRecords;

      cursor.pending = cursor.pending.concat(processedRecords);
    }

    // Outside the block above: the `done` message can arrive with no new
    // rows, and it still has to flush whatever is pending.
    if (
      shouldPublishRows(
        cursor.pending.length,
        cursor.publishedCount,
        worker.isComplete
      )
    ) {
      const toPublish = cursor.pending;
      cursor.pending = [];
      cursor.publishedCount += toPublish.length;
      setData(prev => prev.concat(toPublish));
    }
  }, [worker.rows, worker.columns, worker.isComplete, previewOnly, url]);

  // The single gate: everything below reads `view`, never the raw
  // `data`/`columns`/`previewUnavailable` state directly, so a value left
  // over from a PREVIOUS report/mode can never be rendered under a NEW
  // report's identity, no matter how the two effects above are timed.
  const view = deriveVisibleReportState(
    { data, columns, previewUnavailable, settledKey: settledKeyRef.current },
    { url, previewOnly }
  );

  if (view.previewUnavailable) {
    return <DownloadableReport report={report} />;
  }

  // `!view.isSettledForCurrentReport` forces both flags into their "still
  // waiting" state regardless of what `worker`/`previewQuery` themselves
  // report on this exact commit — closing the specific gap that let a
  // stale-but-not-yet-corrected `worker.isLoading`/`isError` (still a
  // snapshot of the OLD report; see the FRAGILE ORDERING comment above) get
  // paired with `view.data`/`view.columns` that are ALREADY correctly empty.
  const isTabularFileLoading =
    !view.isSettledForCurrentReport ||
    (previewOnly ? isPreviewLoading : worker.isLoading);
  const isTabularFileError =
    view.isSettledForCurrentReport &&
    (previewOnly ? isPreviewError : worker.isError);

  // Preview mode is all-or-nothing (a single small parse), so gate purely on
  // its loading flag, exactly as before. Full-table mode streams
  // progressively: once the first batch has produced columns, show the
  // table immediately (with the loading banner below) rather than waiting
  // for the whole file — that progressive reveal is the point of Task 9.
  const showSkeleton = previewOnly
    ? isTabularFileLoading
    : isTabularFileLoading && view.columns.length === 0;

  return (
    <div className="h-full pb-15 overflow-auto">
      {showSkeleton && <SkeletonLoader count={3} />}
      {previewOnly && !isTabularFileLoading && !isTabularFileError && (
        <div className="mb-2 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
          This file is too large to open in full. Showing the first{" "}
          {view.data.length.toLocaleString("en-US")} rows. Use the download
          button for the complete table.
          {onLoadFullTable && (
            <>
              <button
                type="button"
                id="loadFullTableButton"
                onClick={onLoadFullTable}
                className="ml-2 rounded bg-white px-2 py-0.5 font-semibold text-amber-900 shadow-sm ring-1 ring-inset ring-amber-300 hover:bg-amber-100">
                Load full table
              </button>
              <Tooltip
                anchorSelect="#loadFullTableButton"
                content={describeFullTableLoad(report.fileSize)}
                className="max-w-sm"
              />
            </>
          )}
        </div>
      )}
      {!previewOnly && worker.isLoading && !showSkeleton && (
        <div className="mb-2 text-sm text-gray-600">
          Loading {worker.total.toLocaleString("en-US")} rows…
        </div>
      )}
      {!showSkeleton && !isTabularFileError && (
        <Table
          data={view.data}
          columns={view.columns}
          showPagination
          total={view.data?.length || 0}
          enableGlobalFilter
          emptyMessage="No data available"
          applyColumnSize
          customTableClassName="w-max whitespace-nowrap"
          pageSizeOptions={[5, 10, 20, 100]}
        />
      )}
      {isTabularFileError && (
        <GenericError message={"Error While Loading The File"} />
      )}
    </div>
  );
}
